/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import CommercialProfileContext from './CommercialProfileContext';
import initialCommercialProfileState from './initialCommercialProfileState';
import userReducer from 'contexts/commercialProfile/reducer/commercialProfile.reducer';
import * as action from 'contexts/commercialProfile/reducer/commercialProfile.actions';
import { useTranslation } from 'react-i18next';
import { abortCatalogController } from 'utils/abortController';
import { useAuth0 } from '@auth0/auth0-react';
import * as CommercialProfileTypes from 'contexts/commercialProfile/reducer/commercialProfile.types';
import { initialProductsQuantity } from 'utils/constants';
import useContextUser from 'hooks/contexts/useContextUser';
import { TQuerySearchArray } from 'interfaces/catalog.interface';
import { IChildrenProps } from 'interfaces/general.interface';
import { IfindMultimediaAPIItemResponse } from 'api/endpoints/multimedia/multimedia.response.interface';
import { IgetCatalogResponseDataItem } from 'api/endpoints/catalog/catalog.response.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function CommercialProfileProvider(props: IChildrenProps) {
  const [state, dispatch] = useReducer(userReducer, initialCommercialProfileState);
  const { t: translate } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();

  useEffect(() => {
    if (!company?.brand?.id || !dbUser?.i18n_lang_id) return;
    state.bodyProductSearch.options.brand_id[0] = company.brand?.id;
    state.bodyProductSearch.options.language_id = dbUser.i18n_lang_id;
    dispatch({
      type: CommercialProfileTypes.SET_BODY_PRODUCT,
      payload: {
        brandId: company.brand.id,
        limit: initialProductsQuantity,
        languageId: dbUser.i18n_lang_id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.brand?.id, dbUser?.i18n_lang_id]);

  const getComercialProfileData = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!dbUser?.i18n_lang_id || !company?.brand?.id) return false;
    if (!token) return false;
    return await action.getComercialProfileDataAction(
      dispatch,
      company.brand?.id,
      dbUser.i18n_lang_id,
      translate,
      token
    );
  }, [company?.brand?.id, dbUser?.i18n_lang_id]);

  const setBanner = useCallback(
    async (banner: IfindMultimediaAPIItemResponse) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setBannerAction(dispatch, banner, state, translate, token);
    },
    [state]
  );

  const initBio = useCallback(async () => {
    if (!company?.id || !company?.country?.id) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.initBioAction(
      dispatch,
      company.id,
      company.country.id,
      translate,
      token
    );
  }, [company?.id, company?.country?.id]);

  const updateBio = useCallback(
    async (bio: string) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateBioAction(dispatch, bio, company.id, translate, token);
    },
    [company?.id]
  );

  const setProducts = useCallback(
    async (products: IgetCatalogResponseDataItem[]) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setProductsAction(dispatch, products, state, translate, token);
    },
    [state, getAccessTokenSilently]
  );

  const removeProduct = useCallback(async (product: IgetCatalogResponseDataItem) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.removeProductAction(dispatch, product, translate, token);
  }, []);

  const setVideos = useCallback(
    async (videos: IfindMultimediaAPIItemResponse[]) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setVideosAction(dispatch, videos, state, translate, token);
    },
    [state]
  );

  const removeVideo = useCallback(async (video: IfindMultimediaAPIItemResponse) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.removeVideoAction(dispatch, video, translate, token);
  }, []);

  // Catalog
  const getCatalog = useCallback(async () => {
    if (!state.bodyProductSearch.options?.brand_id[0]) return false;
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getCatalogAction(
      dispatch,
      state.bodyProductSearch,
      translate,
      signal,
      token
    );
  }, [state.bodyProductSearch]);

  const addMoreProductsToCatalog = useCallback(async () => {
    if (!state.bodyProductSearch) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.addMoreProductsToCatalogAction(
      dispatch,
      state.bodyProductSearch,
      translate,
      token
    );
  }, [state.bodyProductSearch]);

  // Filters
  const filter = useCallback(
    async (setOpenDrawer: any) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.filterAction(
        dispatch,
        setOpenDrawer,
        state,
        translate,
        signal,
        token
      );
    },
    [state]
  );

  const addBodyFilters = useCallback(async (field: string, value: string) => {
    return await action.addBodyFiltersAction(dispatch, field, value);
  }, []);

  const removeBodyFilters = useCallback(async (field: string, value: string) => {
    return await action.removeBodyFiltersAction(dispatch, field, value);
  }, []);

  const resetBodyFilters = useCallback(
    async (setOpenDrawer: any) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.resetBodyFiltersAction(
        dispatch,
        state.bodyProductSearch,
        setOpenDrawer,
        translate,
        signal,
        token
      );
    },
    [state.bodyProductSearch]
  );

  const setPriceSliders = useCallback(async (field: string, value: any) => {
    return await action.setPriceSlidersAction(dispatch, field, value);
  }, []);

  const setFavorites = useCallback(async (userId: string | null) => {
    return await action.setFavoritesAction(dispatch, userId);
  }, []);

  const resetPriceSlider = useCallback(
    async (type: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.resetPriceSliderAction(
        dispatch,
        type,
        state.bodyProductSearch,
        translate,
        signal,
        token
      );
    },
    [state.bodyProductSearch]
  );

  // Search engine
  const setQuerySearch = useCallback(async (query: string) => {
    return await action.setQuerySearchAction(dispatch, query);
  }, []);

  const addToBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addToBodySearchAction(
        dispatch,
        query,
        state,
        translate,
        signal,
        token
      );
    },
    [state]
  );

  const removeFromBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromBodySearchAction(
        dispatch,
        query,
        state,
        translate,
        signal,
        token
      );
    },
    [state]
  );

  const removeAllFromBodySearch = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.removeAllFromBodySearchAction(
      dispatch,
      state,
      translate,
      signal,
      token
    );
  }, [state]);

  const removeFromBodySearchFilter = useCallback(
    async (value: any) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromBodySearchFilterAction(
        dispatch,
        value,
        state,
        translate,
        signal,
        token
      );
    },
    [state]
  );

  const addToQuerySearchArray = useCallback(async (value: TQuerySearchArray) => {
    return await action.addToQuerySearchArrayAction(dispatch, value);
  }, []);

  const removeFromQuerySearchArray = useCallback(async (value: TQuerySearchArray) => {
    return await action.removeFromQuerySearchArrayAction(dispatch, value);
  }, []);

  // Sort By
  const setSortBy = useCallback(
    async (sortBy: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setSortByAction(
        dispatch,
        sortBy,
        state.bodyProductSearch,
        translate,
        signal,
        token
      );
    },
    [state.bodyProductSearch]
  );

  const setSortByHaveImages = useCallback(
    async (haveImages: boolean | null) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.setSortByHaveImagesAction(
        dispatch,
        haveImages,
        state.bodyProductSearch,
        translate,
        signal,
        token
      );
    },
    [state.bodyProductSearch]
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      getComercialProfileData,
      setBanner,
      initBio,
      // setBio,
      updateBio,
      setProducts,
      removeProduct,
      setVideos,
      removeVideo,
      // Catalog
      getCatalog,
      addMoreProductsToCatalog,
      // Filters
      filter,
      addBodyFilters,
      removeBodyFilters,
      resetBodyFilters,
      setPriceSliders,
      setFavorites,
      resetPriceSlider,
      // Search Engine
      setQuerySearch,
      addToBodySearch,
      removeFromBodySearch,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      // Sort By
      setSortBy,
      setSortByHaveImages,
    }),
    [
      state,
      getComercialProfileData,
      setBanner,
      initBio,
      // setBio,
      updateBio,
      setProducts,
      removeProduct,
      setVideos,
      removeVideo,
      // Catalog
      getCatalog,
      addMoreProductsToCatalog,
      // Filters
      filter,
      addBodyFilters,
      removeBodyFilters,
      resetBodyFilters,
      setPriceSliders,
      setFavorites,
      resetPriceSlider,
      // Search Engine
      setQuerySearch,
      addToBodySearch,
      removeFromBodySearch,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      // Sort By
      setSortBy,
      setSortByHaveImages,
    ]
  );

  return (
    <CommercialProfileContext.Provider value={memoProvider}>
      {props.children}
    </CommercialProfileContext.Provider>
  );
}

export default memo(CommercialProfileProvider);
